// 'use client';

// import { useEffect } from 'react';
// import { AlertCircle, RefreshCw, Home } from 'lucide-react';
// import { Button } from '@/lib/components/ui/button';
// import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/lib/components/ui/card';
// import { reportErrorToAppStatus } from '@/lib/hooks/useErrorReporting';

// interface GlobalErrorProps {
//   error: Error & { digest?: string };
//   reset: () => void;
// }

// export default function GlobalError({ error, reset }: GlobalErrorProps) {
//   useEffect(() => {
//     // Report error to app_status system
//     reportErrorToAppStatus(error, {
//       component: 'GlobalErrorBoundary',
//       action: 'page_render',
//       error_digest: error.digest,
//     });

//     // Also log to console for debugging
//     console.error('Global error caught:', error);
//   }, [error]);

//   return (
//     <div className="min-h-screen flex items-center justify-center p-4 bg-background">
//       <Card className="max-w-lg w-full">
//         <CardHeader className="text-center">
//           <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
//             <AlertCircle className="h-6 w-6 text-red-600" />
//           </div>
//           <CardTitle>Something went wrong</CardTitle>
//           <CardDescription>
//             An unexpected error occurred. This has been automatically reported to our team.
//           </CardDescription>
//         </CardHeader>
//         <CardContent className="space-y-4">
//           {process.env.NODE_ENV === 'development' && (
//             <div className="bg-muted rounded-lg p-3">
//               <p className="text-sm font-mono text-red-600 break-words">
//                 {error.message}
//               </p>
//               {error.digest && (
//                 <p className="text-xs text-muted-foreground mt-1">
//                   Error ID: {error.digest}
//                 </p>
//               )}
//             </div>
//           )}

//           <div className="flex gap-2 justify-center">
//             <Button onClick={reset} variant="default" className="gap-2">
//               <RefreshCw className="h-4 w-4" />
//               Try Again
//             </Button>
//             <Button
//               onClick={() => {
//                 window.location.href = '/';
//               }}
//               variant="outline"
//               className="gap-2"
//             >
//               <Home className="h-4 w-4" />
//               Go Home
//             </Button>
//           </div>

//           <p className="text-xs text-center text-muted-foreground">
//             If this problem persists, please use the feedback button to report additional details.
//           </p>
//         </CardContent>
//       </Card>
//     </div>
//   );
// }

// 'use client';

// import { useEffect } from 'react';
// import { AlertCircle, RefreshCw, Home } from 'lucide-react';
// import { Button } from '@/lib/components/ui/button';
// import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/lib/components/ui/card';
// import { reportErrorToAppStatus } from '@/lib/hooks/useErrorReporting';

// interface GlobalErrorProps {
//   error: Error & { digest?: string };
//   reset: () => void;
// }

// export default function GlobalError({ error, reset }: GlobalErrorProps) {
//   useEffect(() => {
//     reportErrorToAppStatus(error, {
//       component: 'GlobalErrorBoundary',
//       action: 'page_render',
//       error_digest: error.digest,
//     });

//     console.error('Global error caught:', error);
//   }, [error]);

//   return (
//     <div className='fixed inset-0 flex items-center justify-center p-4 bg-background'>
//       <Card className='max-w-lg w-full'>
//         <CardHeader className='text-center'>
//           <div className='mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center'>
//             <AlertCircle className='h-6 w-6 text-red-600' />
//           </div>
//           <CardTitle>Something went wrong</CardTitle>
//           <CardDescription>
//             An unexpected error occurred. This has been automatically reported to our team.
//           </CardDescription>
//         </CardHeader>

//         <CardContent className='space-y-4'>
//           {process.env.NODE_ENV === 'development' && (
//             <div className='bg-muted rounded-lg p-3'>
//               <p className='text-sm font-mono text-red-600 break-words'>{error.message}</p>
//               {error.digest && <p className='text-xs text-muted-foreground mt-1'>Error ID: {error.digest}</p>}
//             </div>
//           )}

//           <div className='flex gap-2 justify-center'>
//             <Button onClick={reset} variant='default' className='gap-2'>
//               <RefreshCw className='h-4 w-4' />
//               Try Again
//             </Button>

//             <Button onClick={() => (window.location.href = '/')} variant='outline' className='gap-2'>
//               <Home className='h-4 w-4' />
//               Go Home
//             </Button>
//           </div>

//           <p className='text-xs text-center text-muted-foreground'>
//             If this problem persists, please use the feedback button to report additional details.
//           </p>
//         </CardContent>
//       </Card>
//     </div>
//   );
// }

'use client';

import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/lib/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/lib/components/ui/card';
import { reportErrorToAppStatus } from '@/lib/hooks/useErrorReporting';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    reportErrorToAppStatus(error, {
      component: 'GlobalErrorBoundary',
      action: 'page_render',
      error_digest: error.digest,
    });

    console.error('Global error caught:', error);
  }, [error]);

  return (
    <div
      className='
        fixed inset-0 flex items-center justify-center p-6
        dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900
        animate-[gradientShift_6s_ease-in-out_infinite]
      '
    >
      <Card
        className='
          max-w-lg w-full shadow-2xl backdrop-blur-md
          animate-[cardPop_0.5s_ease-out]
        '
      >
        <CardHeader className='text-center space-y-3'>
          <div
            className='
              mx-auto h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30
              flex items-center justify-center shadow-inner
              animate-[float_3s_ease-in-out_infinite]
            '
          >
            <AlertTriangle className='h-8 w-8 text-red-600 dark:text-red-400 animate-[pulse_2s_infinite]' />
          </div>

          <CardTitle className='text-2xl font-semibold'>Something went wrong</CardTitle>

          <CardDescription className='text-base'>
            We ran into an unexpected issue. Our team has been notified.
          </CardDescription>
        </CardHeader>

        <CardContent className='space-y-6'>
          {/* Toggle for error details */}
          <div className='flex justify-center'>
            <Button
              variant='ghost'
              className='text-sm flex items-center gap-1'
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
              Technical Details
            </Button>
          </div>

          <div
            className={`
              overflow-hidden transition-all duration-300
              ${showDetails ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}
            `}
          >
            <div className='bg-muted rounded-lg p-4 border text-sm font-mono text-red-700 dark:text-red-300'>
              <p>{error.message}</p>
              {error.digest && <p className='text-xs text-muted-foreground mt-2'>Error ID: {error.digest}</p>}
            </div>
          </div>

          <div className='flex gap-3 justify-center'>
            <Button onClick={reset} variant='default' className='gap-2'>
              <RefreshCw className='h-4 w-4' />
              Try Again
            </Button>

            <Button onClick={() => (window.location.href = '/')} variant='outline' className='gap-2'>
              <Home className='h-4 w-4' />
              Go Home
            </Button>
          </div>

          <p className='text-xs text-center text-muted-foreground'>
            If this keeps happening, please send us feedback so we can investigate.
          </p>
        </CardContent>
      </Card>

      {/* Custom keyframes */}
      <style jsx global>{`
        @keyframes cardPop {
          0% {
            opacity: 0;
            transform: scale(0.85) translateY(20px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes float {
          0% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-6px);
          }
          100% {
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </div>
  );
}
